import React, { useEffect, useState } from "react";
import { apiPut } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Save, Pencil } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only inline editor for a security's fundamentals.
 *
 * Renders a Pencil button which opens a Dialog. Each field is a number input;
 * empty fields are sent as `null` (clears the value). On save:
 *   PUT /api/stocks/{cse_symbol}/fundamentals
 * Dividend yield is auto-recalculated by the backend from DPS / close price.
 */
const FIELDS = [
  { key: "pe_ratio",          label: "P/E Ratio",            step: "0.01" },
  { key: "pb_ratio",          label: "P/B Ratio",            step: "0.01" },
  { key: "eps",               label: "EPS — Earnings Per Share (LKR)", step: "0.01" },
  { key: "roe",               label: "ROE — Return on Equity (%)", step: "0.01" },
  { key: "dividend_per_share",label: "Dividend Per Share (LKR)",  step: "0.01" },
  { key: "debt_to_equity",    label: "Debt to Equity",        step: "0.01" },
  { key: "revenue_growth",    label: "Revenue Growth (%)",    step: "0.01" },
  { key: "earnings_growth",   label: "Earnings Growth (%)",   step: "0.01" },
];

const EditFundamentalsDialog = ({
  cseSymbol,
  currentFund,
  onSaved,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  hideTrigger = false,
}) => {
  const [openState, setOpenState] = useState(false);
  // Allow both uncontrolled (self-rendered pencil trigger) and controlled
  // (parent owns the open flag; we only render the dialog body). Reports
  // tab uses the controlled variant because its own button sits next to
  // "Read Analysis".
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = (v) => {
    if (onOpenChangeProp) onOpenChangeProp(v);
    if (openProp === undefined) setOpenState(v);
  };
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const seed = () => {
    const next = {};
    for (const f of FIELDS) {
      const v = currentFund?.[f.key];
      next[f.key] = v == null ? "" : String(v);
    }
    setForm(next);
  };

  useEffect(() => { if (open) seed(); /* eslint-disable-next-line */ }, [open, currentFund]);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {};
    for (const f of FIELDS) {
      const raw = form[f.key];
      if (raw === "" || raw == null) {
        body[f.key] = null; // clear
        continue;
      }
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        toast.error(`"${f.label}" must be a number`);
        setSaving(false);
        return;
      }
      body[f.key] = n;
    }
    try {
      const res = await apiPut(`/stocks/${cseSymbol}/fundamentals`, body);
      toast.success("Fundamentals updated and score recalculated", {
        description: `${cseSymbol} — yield auto-recomputed from DPS / close price.`,
      });
      setOpen(false);
      onSaved?.(res?.fundamentals);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => setOpen(true)}
          data-testid="edit-fundamentals-button"
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit Fundamentals
        </Button>
      )}
      <DialogContent
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid="edit-fundamentals-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Pencil className="h-4 w-4 text-primary" />
            Edit Fundamentals — {cseSymbol}
          </DialogTitle>
          <DialogDescription>
            Manually enter or update fundamental data. Dividend yield will be
            recalculated automatically from DPS and the latest close price. The
            stock score is regenerated after saving.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`fund-${f.key}`} className="text-xs">{f.label}</Label>
                <Input
                  id={`fund-${f.key}`}
                  type="number"
                  inputMode="decimal"
                  step={f.step}
                  value={form[f.key] ?? ""}
                  onChange={onChange(f.key)}
                  placeholder="—"
                  className="h-9 tabular"
                  data-testid={`fund-input-${f.key}`}
                />
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Leave a field blank to clear it. Yield is auto-derived (DPS / close × 100) and not
            editable directly.
          </p>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={saving} data-testid="edit-fundamentals-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2" data-testid="edit-fundamentals-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save Fundamentals
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditFundamentalsDialog;
